<template>
    <div class="bg-white">
        <CRMProgressUpdatePage 
            v-for="(progressUpdatePage, index) in pages"  
            :key="index"
            :clientName="clientName"
            :numReportedCompanies="numReportedCompanies"
            :companyLabel="companyLabel"
            :numCampaigns="numCampaigns"
            :campaignLabel="campaignLabel"
            :progressUpdatePage="progressUpdatePage"
        />
    </div>
</template>

<script setup>
import { defineProps, ref, reactive, watch, computed } from 'vue';
import CRMProgressUpdatePage from '@/components/crm-reporting/print-components/CRMProgressUpdatePage.vue'

const props = defineProps([
    'clientName',
    'numReportedCompanies',
    'companyLabel',
    'numCampaigns',
    'campaignLabel',
    'progressUpdates'
])

const maxPageLines = ref(57)
const maxLineLength = ref(50)

const progressUpdatesByStatus = ref([])

function getProgressUpdatesByStatus() {
    progressUpdatesByStatus.value = progressUpdatesByStatus.value.concat(
        props.progressUpdates.filter((progressUpdate) => {
            return (progressUpdate.company.computed_crm_status.label == 'Active - In Dialogue' || 
            progressUpdate.company.computed_crm_status.label == 'Under LOI' || 
            progressUpdate.company.computed_crm_status.label == 'IOI Submitted');
        }).map((progressUpdate) => {
            return {
                ...progressUpdate,
                status: 'Active - In Dialogue'
            }
        })
    ).sort((a, b) => a.company.company_name - b.company.company_name)
    progressUpdatesByStatus.value = progressUpdatesByStatus.value.concat(
        props.progressUpdates.filter((progressUpdate) => {
            return progressUpdate.company.computed_crm_status.label == 'Responded / Coverage';
        }).map((progressUpdate) => {
            return {
                ...progressUpdate,
                status: 'Responded / Coverage'
            }
        })
    ).sort((a, b) => a.company.company_name - b.company.company_name)
    progressUpdatesByStatus.value = progressUpdatesByStatus.value.concat(
        props.progressUpdates.filter((progressUpdate) => {
            return progressUpdate.company.computed_crm_status.label == 'Not Now / Keep Warm' ||
            progressUpdate.company.computed_crm_status.label == 'Dead';
        }).map((progressUpdate) => {
            return {
                ...progressUpdate,
                status: 'Not Now / Keep Warm / Dead'
            }
        })
    ).sort((a, b) => a.company.company_name - b.company.company_name)
    progressUpdatesByStatus.value = progressUpdatesByStatus.value.concat(
        props.progressUpdates.filter((progressUpdate) => {
            return progressUpdate.company.computed_crm_status.label == 'Not Contacted' || 
            progressUpdate.company.computed_crm_status.label == 'Revist / Cut' || 
            progressUpdate.company.computed_crm_status.label == 'Dead';
        }).map((progressUpdate) => {
            return {
                ...progressUpdate,
                status: 'Not Contacted / Revisit / Dead'
            }
        })
    ).sort((a, b) => a.company.company_name - b.company.company_name)
    progressUpdatesByStatus.value = progressUpdatesByStatus.value.concat(
        props.progressUpdates.filter((progressUpdate) => {
            return progressUpdate.company.computed_crm_status.label == 'Client Pass';
        }).map((progressUpdate) => {
            return {
                ...progressUpdate,
                status: 'Client Pass'
            }
        })
    ).sort((a, b) => a.company.company_name - b.company.company_name)
    progressUpdatesByStatus.value = progressUpdatesByStatus.value.concat(
        props.progressUpdates.filter((progressUpdate) => {
            return progressUpdate.company.computed_crm_status.label == 'Not Interested';
        }).map((progressUpdate) => {
            return {
                ...progressUpdate,
                status: 'Not Interested'
            }
        })
    ).sort((a, b) => a.company.company_name - b.company.company_name)
    progressUpdatesByStatus.value = progressUpdatesByStatus.value.concat(
        props.progressUpdates.filter((progressUpdate) => {
            return progressUpdate.company.computed_crm_status.label == 'No Response';
        }).map((progressUpdate) => {
            return {
                ...progressUpdate,
                status: 'No Response'
            }
        })
    ).sort((a, b) => a.company.company_name - b.company.company_name)
}

const statusGroups = reactive({
    activeInDialogue: {
        title: 'Active - In Dialogue',
        progressUpdates: props.progressUpdates.filter((progressUpdate) => {
            return (progressUpdate.company.computed_crm_status.label == 'Active - In Dialogue' || 
            progressUpdate.company.computed_crm_status.label == 'Under LOI' || 
            progressUpdate.company.computed_crm_status.label == 'IOI Submitted');
        }),
        ordinal: 0,
        columns: []
    },
    respondedCoverage: {
        title: 'Responded / Coverage',
        progressUpdates: props.progressUpdates.filter((progressUpdate) => {
            return progressUpdate.company.computed_crm_status.label == 'Responded / Coverage';
        }),
        ordinal: 1,
        columns: []
    },
    notNowKeepWarm: {
        title: 'Not Now / Keep Warm',
        progressUpdates: props.progressUpdates.filter((progressUpdate) => {
            return progressUpdate.company.computed_crm_status.label == 'Not Now / Keep Warm';
        }),
        ordinal: 2,
        columns: []
    },
    notContactedRevisit: {
        title: 'Not Contacted / Revisit / Dead',
        progressUpdates: props.progressUpdates.filter((progressUpdate) => {
            return (progressUpdate.company.computed_crm_status.label == 'Not Contacted' || progressUpdate.company.computed_crm_status.label == 'Revisit / Cut' || progressUpdate.company.computed_crm_status.label == 'Dead');
        }),
        ordinal: 3,
        columns: []
    },
    // deadAcquired: {
    //     title: 'Dead / Acquired',
    //     progressUpdates: props.progressUpdates.filter((progressUpdate) => {
    //         return progressUpdate.company.computed_crm_status.label == 'Dead';
    //     }),
    //     columns: []
    // },
    clientPassed: {
        title: 'Client Pass',
        progressUpdates: props.progressUpdates.filter((progressUpdate) => {
            return progressUpdate.company.computed_crm_status.label == 'Client Pass';
        }),
        ordinal: 4,
        columns: []
    },
    targetPassed: {
        title: 'Not Interested',
        progressUpdates: props.progressUpdates.filter((progressUpdate) => {
            return progressUpdate.company.computed_crm_status.label == 'Not Interested';
        }),
        ordinal: 5,
        columns: []
    },
    nonRespondents: {
        title: 'Non-Respondents',
        progressUpdates:  props.progressUpdates.filter((progressUpdate) => {
            return progressUpdate.company.computed_crm_status.label == 'No Response';
        }),
        ordinal: 6,
        columns: []
    }
})

const pages = ref([])
const lastPage = ref(false)
const startingIndex = ref(0)

function buildPages() {
    getProgressUpdatesByStatus()
    while(progressUpdatesByStatus.value.length > 0) {
        let page = buildPage(progressUpdatesByStatus.value)
        if(lastPage.value) {
            Object.values(statusGroups).forEach((statusGroup) => {
            if(statusGroup.progressUpdates.length == 0) {
                page.sections.push({title: statusGroup.title, columns: [{startingIndex: 0, progressUpdates: []}, {startingIndex: 0, progressUpdates: []}, {startingIndex: 0, progressUpdates: []},]})
            }
        })
        }
        pages.value.push(page)
    }
}

function buildPage(remainingProgressUpdates) {
    let page = {
        sections: [],
        remainingLines: maxPageLines.value
    }
    while(page.remainingLines > 2 && remainingProgressUpdates.length > 0) {
        let crmStatus = remainingProgressUpdates[0].status
        if(!pages.value.some((page) => { 
            return page.sections.some((section) => { 
                return section.title == crmStatus 
            }
        )})) {
            startingIndex.value = 0
        }
        let numProgressUpdatesByCurrentStatus = remainingProgressUpdates.filter(progressUpdate => progressUpdate.status == crmStatus).length
        let remainingProgressUpdatesInCurrentStatus = remainingProgressUpdates.slice(0, numProgressUpdatesByCurrentStatus)
        let maxColumnLines = getMaxColumnLinesForSection(remainingProgressUpdatesInCurrentStatus, page.remainingLines)

        // New
        if(page.remainingLines > maxColumnLines) {
            console.log('maxColumnLines', maxColumnLines)
            let newSection = buildSectionFromUpdates(crmStatus, remainingProgressUpdates, maxColumnLines)
            console.log('section', newSection)
            page.sections.push(newSection)
            page.remainingLines = page.remainingLines - maxColumnLines
            console.log('remaining lines', page.remainingLines)
        } else {
            return page
        }
        // --
    }
    if(remainingProgressUpdates.length == 0) {
        lastPage.value = true
    }
    return page
}

function buildSectionFromUpdates(crmStatus, progressUpdates, maxLines) {
    let section = {
        title: crmStatus,
        columns: [],
    }
    while(section.columns.length < 3) {
        let column = buildSectionColumn(progressUpdates, maxLines, crmStatus)
        console.log('column')
        console.log(column)
        console.log('title', crmStatus)
        section.columns.push(column)
    }
    return section
}

function buildSectionColumn(progressUpdates, maxLines, crmStatus) {
    let column = {
        startingIndex: startingIndex.value,
        progressUpdates: []
    }
    let columnLines = 0
    while(columnLines < maxLines && progressUpdates.length > 0) {
        let progressUpdateToAdd = progressUpdates[0]
        if(progressUpdateToAdd.status != crmStatus) {
            break
        }
        let progressUpdateLines = 1 + Math.ceil(progressUpdateToAdd.body.length / maxLineLength.value)
        column.progressUpdates.push(progressUpdateToAdd)
        progressUpdates.shift()
        columnLines += progressUpdateLines
    }
    startingIndex.value += column.progressUpdates.length
    return column
}

function getMaxColumnLinesForSection(progressUpdatesByStatus, remainingLines) {
    console.log(progressUpdatesByStatus, remainingLines)
    let initialLines = 0
    let progressUpdatesMaxColumnLines = Math.ceil(progressUpdatesByStatus.reduce((acc, progressUpdate) => {
        return acc + getLinesByProgressUpdate(progressUpdate)
    }, initialLines) / 3)
    return progressUpdatesMaxColumnLines > remainingLines ? remainingLines : progressUpdatesMaxColumnLines
}

function getLinesByProgressUpdate(progressUpdate) {
    return 1 + Math.ceil(progressUpdate.body.length / maxLineLength.value)
}

buildPages()

</script>